import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState } from 'react';
import CheckCircleIcon from '@heroicons/react/outline/CheckCircleIcon';
import ExclamationIcon from '@heroicons/react/outline/ExclamationIcon';
import EyeOffIcon from '@heroicons/react/outline/EyeOffIcon';
import PencilAltIcon from '@heroicons/react/outline/PencilAltIcon';
import TrashIcon from '@heroicons/react/outline/TrashIcon';
import { hasScopesPermission } from '@tribeplatform/gql-client/lib';
import { ModerationEntityType, PinnedInto, } from '@tribeplatform/gql-client/types';
import { useDeletePost, useHidePost, useNetwork, usePinReplyToPost, useUnhidePost, useUnpinReplyFromPost, } from '@tribeplatform/react-sdk/hooks';
import { Dropdown } from '@tribeplatform/react-ui-kit/Dropdown';
import { toast } from '@tribeplatform/react-ui-kit/Toast';
import { T, useI18n } from '../i18n';
import { ReportModal } from '../ReportModal';
export const ReplyOptions = ({ reply, onEdit, post, }) => {
    var _a;
    const intl = useI18n();
    const { mutateAsync: deletePost } = useDeletePost();
    const { mutateAsync: hidePost } = useHidePost();
    const { mutateAsync: unhidePost } = useUnhidePost();
    const { mutateAsync: pinReply } = usePinReplyToPost();
    const { mutateAsync: unpinReply } = useUnpinReplyFromPost();
    const { data: network } = useNetwork();
    const [reportModal, setReportModal] = useState(false);
    const isPinned = (_a = reply === null || reply === void 0 ? void 0 : reply.pinnedInto) === null || _a === void 0 ? void 0 : _a.includes(PinnedInto.post);
    const [canUpdatePost, canRemovePost, canHidePost] = hasScopesPermission(reply, ['updatePost', 'removePost', 'hidePost']);
    const [canReportPost] = hasScopesPermission(network, [
        'createModerationReport',
    ]);
    const [canPinReplyToPost] = hasScopesPermission(post, ['pinReplyToPost']);
    const removePost = () => {
        if (window.confirm(intl.$t({
            id: 'Reply.ReplyOptions.ReplyDeletionConfirmText',
            defaultMessage: 'Are you sure you want to delete this reply?',
        }))) {
            deletePost({
                id: reply.id,
            });
        }
    };
    const toggleHidePost = () => {
        if (reply.isHidden) {
            unhidePost({
                id: reply.id,
            });
        }
        else {
            hidePost({
                id: reply.id,
            });
        }
    };
    const togglePin = async () => {
        var _a, _b, _c;
        const rootPostId = (_a = reply === null || reply === void 0 ? void 0 : reply.repliedToIds) === null || _a === void 0 ? void 0 : _a[(reply === null || reply === void 0 ? void 0 : reply.repliedToIds.length) - 1];
        try {
            if (isPinned) {
                await unpinReply({
                    replyId: reply === null || reply === void 0 ? void 0 : reply.id,
                    postId: rootPostId,
                });
            }
            else {
                await pinReply({
                    replyId: reply === null || reply === void 0 ? void 0 : reply.id,
                    postId: rootPostId,
                });
            }
        }
        catch (error) {
            if ((_c = (_b = error === null || error === void 0 ? void 0 : error.response) === null || _b === void 0 ? void 0 : _b.errors) === null || _c === void 0 ? void 0 : _c.find(e => e.code === '102')) {
                return toast({
                    title: intl.$t({
                        id: 'Reply.ReplyOptions.CantMakeMultipleRepliesAsBestTitle',
                        defaultMessage: 'Cannot mark multiple replies as best reply',
                    }),
                    description: intl.$t({
                        id: 'Reply.ReplyOptions.CantMakeMultipleRepliesAsBestDescription',
                        defaultMessage: 'Please unmark the current best reply and try again.',
                    }),
                    status: 'error',
                });
            }
            toast({
                title: intl.$t({
                    id: 'Generics.SomethingWentWrong',
                    defaultMessage: 'Something went wrong',
                }),
                description: intl.$t({
                    id: 'Generics.TryAgainLater',
                    defaultMessage: 'Please try again later.',
                }),
                status: 'error',
            });
        }
    };
    if (!canRemovePost &&
        !canUpdatePost &&
        !canHidePost &&
        !canPinReplyToPost &&
        !canReportPost) {
        return null;
    }
    return (_jsxs(_Fragment, { children: [_jsxs(Dropdown, { placement: "bottom-end", children: [_jsx(Dropdown.ButtonMinimal, { className: "mx-2 bg-transparent" }, void 0), _jsxs(Dropdown.Items, { children: [canUpdatePost && (_jsx(Dropdown.Item, { leadingIcon: _jsx(PencilAltIcon, {}, void 0), onClick: onEdit, children: _jsx(T, { id: "Generics.Edit", defaultMessage: "Edit" }, void 0) }, void 0)), canRemovePost && (_jsx(Dropdown.Item, { onClick: removePost, leadingIcon: _jsx(TrashIcon, {}, void 0), children: _jsx(T, { id: "Generics.Delete", defaultMessage: "Delete" }, void 0) }, void 0)), canHidePost && (_jsx(Dropdown.Item, { leadingIcon: _jsx(EyeOffIcon, {}, void 0), onClick: toggleHidePost, children: reply.isHidden
                                    ? intl.$t({
                                        id: 'Generics.Unhide',
                                        defaultMessage: 'Unhide',
                                    })
                                    : intl.$t({
                                        id: 'Generics.Hide',
                                        defaultMessage: 'Hide',
                                    }) }, void 0)), canPinReplyToPost && (_jsx(Dropdown.Item, { leadingIcon: _jsx(CheckCircleIcon, {}, void 0), onClick: togglePin, children: isPinned ? (_jsx(T, { id: "Reply.ReplyOptions.RemoveAsBest", defaultMessage: "Remove as best reply" }, void 0)) : (_jsx(T, { id: "Reply.ReplyOptions.MarkAsBest", defaultMessage: "Mark as best reply" }, void 0)) }, void 0)), canReportPost && (_jsx(Dropdown.Item, { leadingIcon: _jsx(ExclamationIcon, {}, void 0), onClick: () => setReportModal(true), children: _jsx(T, { id: "Reply.ReplyOptions.ReportReplyButtonText", defaultMessage: "Report reply" }, void 0) }, void 0))] }, void 0)] }, void 0), reportModal && (_jsx(ReportModal, { entity: reply, entityType: ModerationEntityType.POST, onClose: () => setReportModal(false), open: true }, void 0))] }, void 0));
};
//# sourceMappingURL=ReplyOptions.js.map